import * as React from 'react';

import { readSession, removeSession, writeSession } from '../lib/session-store';

/**
 * Form state that survives a reload, for a form long enough that losing it
 * would cost the visitor real effort.
 *
 * Works like `useState`, and also mirrors the value to `sessionStorage` under
 * `storageName`: restored when the form mounts again in the same tab, gone
 * when the tab closes, and removed by `clear()` — call it once the submission
 * has gone through. While the value equals `initial` nothing is stored, so an
 * untouched form leaves no trace.
 *
 * This stores what the visitor typed — personal details included — so use it
 * only where the form earns it. A short form should rely on
 * `useIdempotencyKey` alone and let the visitor retype.
 */
export function useSessionDraft<T>(storageName: string, initial: T) {
  // Read once: the same object on every render, so it can be compared below.
  const initialRef = React.useRef(initial);
  const [draft, setDraft] = React.useState<T>(
    () => readSession<T>(storageName) ?? initialRef.current,
  );

  React.useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(initialRef.current)) removeSession(storageName);
    else writeSession(storageName, draft);
  }, [draft, storageName]);

  const clear = React.useCallback(() => {
    removeSession(storageName);
    setDraft(initialRef.current);
  }, [storageName]);

  return [draft, setDraft, clear] as const;
}
